// index.ts
import "construct-style-sheets-polyfill";
import md5 from "md5";
import { CamelCaseDashCasePlugin } from "./plugins/camelcase-to-dashcase";
import { FontFacePlugin } from "./plugins/font-face";
import { KeyframesPlugin } from "./plugins/keyframes";
import { MediaQueryPlugin } from "./plugins/media-query";
import { VendorPrefixerPlugin } from "./plugins/vendor-prefixer";
var noModifier = "__no_modifier";
var cache = /* @__PURE__ */ new Map();
var ASS = class {
  constructor(flattenedStyles, className) {
    this.flattenedStyles = flattenedStyles;
    this.className = className;
  }
  static registerPlugin(plugin, index) {
    if (index)
      this.plugins.splice(index, 0, plugin);
    else
      this.plugins.push(plugin);
  }
  static makeSheet(style, enforceClassName = false, elementName) {
    const cacheKey = JSON.stringify(style);
    const cacheResult = cache.get(cacheKey);
    if (cacheResult)
      return cacheResult;
    const className = enforceClassName || this.isClassNameNecessary(style) ? this.generateClassName(style) : void 0;
    if (className)
      style = { [`${elementName ?? ""}.${className}`]: style };
    const styles = createPathStyles(style);
    const computedStyle = { [noModifier]: {} };
    styles.forEach((pathStyle) => {
      for (const plugin of this.plugins) {
        if (plugin.matchModifier(pathStyle.path) && plugin.generateFlattenedStyle(pathStyle, computedStyle))
          return;
      }
      computedStyle[noModifier][this.pathToSelector(pathStyle.path)] = pathStyle.style;
    });
    const acss = new this(computedStyle, className);
    cache.set(cacheKey, acss);
    return acss;
  }
  static pathToSelector(path) {
    return path.reduce((acc, curr, index) => {
      if (acc === "") {
        if (curr === ":host" && index !== path.length - 1 && path[index + 1]?.startsWith("&"))
          return ":host(";
        return curr;
      }
      if (curr.startsWith("&")) {
        if (/:host[\(]{0,1}[^\)]*$/.test(acc)) {
          if (acc === ":host")
            acc += `(${curr.replace("&", "")}`;
          else
            acc += curr.replace("&", "");
        } else
          acc += curr.replace("&", "");
      } else {
        if (/:host[\(]{1}[^\)]*$/.test(acc))
          acc += ") " + curr;
        else
          acc += " " + curr;
      }
      if (index === path.length - 1 && /:host[\(]{1}[^\)]*$/.test(acc))
        acc += ")";
      return acc;
    }, "");
  }
  static stringifyRule(singleRule) {
    const selectors = Object.keys(singleRule);
    if (selectors.length !== 1)
      throw new Error("stringifyRule accepts only one single css rule");
    return `${selectors[0]} {${Object.keys(singleRule[selectors[0]] ?? {}).reduce(
      (acc, styleProperty) => acc + ` ${styleProperty}: ${singleRule[selectors[0]][styleProperty]};`,
      ""
    )} }`;
  }
  static isClassNameNecessary(style) {
    return Object.keys(style ?? {}).some((styleKey) => typeof style[styleKey] !== "object");
  }
  static generateClassName(style) {
    const md5Checksum = md5(JSON.stringify(style));
    return `acss-${md5Checksum.slice(0, 2)}${md5Checksum.slice(-2)}`;
  }
  toString() {
    return Array.from(this.toSheet().cssRules).map((cssRule) => cssRule.cssText).join("\n");
  }
  toSheet() {
    if (this._sheet)
      return this._sheet;
    const sheet = new CSSStyleSheet();
    const constr = this.constructor;
    Object.keys(this.flattenedStyles).forEach((modifier) => {
      for (const plugin of constr.plugins) {
        if (plugin.matchModifier(modifier) && plugin.generateStyleSheetRule(modifier, this.flattenedStyles[modifier], sheet))
          return;
      }
    });
    const noModifierStyle = this.flattenedStyles[noModifier];
    Object.keys(noModifierStyle).map((selector) => ({ [selector]: noModifierStyle[selector] })).forEach((rule, index) => sheet.insertRule(constr.stringifyRule(rule), index));
    this._sheet = sheet;
    return sheet;
  }
};
ASS.plugins = [
  new VendorPrefixerPlugin(),
  new CamelCaseDashCasePlugin(),
  new MediaQueryPlugin(),
  new FontFacePlugin(),
  new KeyframesPlugin()
];
var ASS_PRIMITIVE = Symbol("is an ASS primitive");
var createPathStyles = (style, currentPath = [], existingPathStyles = []) => {
  Object.keys(style || {}).forEach((stylePartKey) => {
    const stylePart = style[stylePartKey];
    if (typeof stylePart !== "object" || stylePart[ASS_PRIMITIVE]) {
      getEntryFromPathStyles(currentPath, existingPathStyles).style[stylePartKey] = stylePart;
    } else {
      createPathStyles(stylePart, [...currentPath, stylePartKey], existingPathStyles);
    }
  });
  return existingPathStyles;
};
var getEntryFromPathStyles = (path, pathStyles) => {
  const entry = pathStyles.find(
    (pathStyle) => pathStyle.path.length === path.length && pathStyle.path.every((pathPart, i) => pathPart === path[i])
  );
  if (entry)
    return entry;
  const newEntry = { path, style: {} };
  pathStyles.push(newEntry);
  return newEntry;
};
export {
  ASS,
  ASS_PRIMITIVE,
  noModifier
};
